import { useEffect, useRef } from 'react';
import { getBroadcastHub } from '../lib/realtime/webHub';

export interface UseBroadcastSpec {
    /** Sujets exacts, tous de la même visibilité. Liste vide = aucun abonnement. */
    topics: readonly string[];
    isPrivate: boolean;
    /** Compte pour lequel les sujets privés ont été calculés ; sans lui, aucun sujet privé n'est rejoint. */
    ownerId: string | null;
    events: readonly string[];
    onMessage: (topic: string, event: string, payload: Record<string, unknown>) => void;
    onResync?: (topic: string) => void;
}

/**
 * Abonne le composant à des sujets du hub. Ajouter ou retirer un sujet ne
 * touche pas les autres (pas de départ / retour sur un canal inchangé) ;
 * changer de compte, de visibilité ou d'événements réabonne tout. Les
 * gestionnaires les plus récents sont utilisés, sans réabonnement.
 */
export function useBroadcast(spec: UseBroadcastSpec): void {
    const specRef = useRef(spec);
    specRef.current = spec;
    const subsRef = useRef(new Map<string, () => void>());
    const identityRef = useRef('');

    const { isPrivate, ownerId } = spec;
    const topicsKey = Array.from(new Set(spec.topics)).sort().join('|');
    const eventsKey = Array.from(new Set(spec.events)).sort().join('|');
    const enabled = !isPrivate || !!ownerId;
    const identity = `${isPrivate}|${ownerId ?? ''}|${eventsKey}`;

    useEffect(() => {
        const subs = subsRef.current;
        if (identityRef.current !== identity) {
            for (const off of subs.values()) off();
            subs.clear();
            identityRef.current = identity;
        }
        const wanted = new Set(enabled && topicsKey ? topicsKey.split('|') : []);
        for (const [topic, off] of [...subs]) {
            if (!wanted.has(topic)) {
                off();
                subs.delete(topic);
            }
        }
        if (wanted.size === 0) return;
        const hub = getBroadcastHub();
        const events = eventsKey ? eventsKey.split('|') : [];
        for (const topic of wanted) {
            if (subs.has(topic)) continue;
            subs.set(
                topic,
                hub.subscribe({
                    topic,
                    isPrivate,
                    ownerId: isPrivate ? ownerId : null,
                    events,
                    onMessage: (event, payload) => specRef.current.onMessage(topic, event, payload),
                    onResync: () => specRef.current.onResync?.(topic),
                }),
            );
        }
    }, [topicsKey, eventsKey, identity, enabled, isPrivate, ownerId]);

    useEffect(() => {
        const subs = subsRef.current;
        return () => {
            for (const off of subs.values()) off();
            subs.clear();
            identityRef.current = '';
        };
    }, []);
}
